/* ═══════════════════════════════════════════════════════════════════════════
   Start.jsx — The one-step evergreen door for GWA Camp
   Route: /#/start  (this is the ad destination)

   ── FIXES IN THIS VERSION ────────────────────────────────────────────────
   1. Webhook URL is the APEX domain (no www). A www→apex 301 kills the CORS
      preflight before PHP ever runs.
   2. Only 'Content-Type' is sent as a header. The Authorization / X-GWA-Secret
      / X-GWA-Token headers were never read by the PHP and broke preflight
      because the Hub sends no Access-Control-Allow-Headers for them.
   3. The secret travels in the BODY as `secret` — which is what the PHP
      actually reads. No ?secret= in the URL (it leaks into access logs).
   4. app_slug is 'gwaacademt'
   5. Response is read as `auth_token`, not `token`.
   6. All redirects point at camp.gospelwiseauthor.com, not camp.faibowrimo.com.
   ─────────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { computeTrailStartDate } from '../../utils/campLogic';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

// TODO: swap in the real evergreen AAP product id before launch
const EVERGREEN_AAP_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/EVERGREEN_AAP_PRODUCT_ID';

const HUB_WEBHOOK_URL = 'https://gospelwiseauthor.app/gwa-camp-register.php';
const CAMP_APP_URL    = 'https://camp.gospelwiseauthor.com';
const HUB_LOGIN_URL   = `https://gospelwiseauthor.app/#/login?redirect=${CAMP_APP_URL}`;

export default function Start() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [evergreenOpen, setEvergreenOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [existingEmail, setExistingEmail] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_gwaacademy');
        const data = Array.isArray(res) ? res : res.data || [];
        const setting = data.find(s => s.key === 'evergreen_registration_open' || s.setting_key === 'evergreen_registration_open');
        if (setting && setting.value === 'false') setEvergreenOpen(false);
      } catch (e) {
        console.error('Failed to check evergreen registration status', e);
      } finally {
        setCheckingReg(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Opening the academy gate...</p>
        </div>
      </div>
    );
  }

  if (!evergreenOpen) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber">
            <SafeIcon name="Tent" className="text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-2">A Live Academy Is In Session</h1>
          <p className="text-lg text-muted mb-6 leading-relaxed">
            Self-paced registration is paused while a live round of Camp FaiBoWriMo is underway.
          </p>
          <a href="https://join.crossandpen.com/p/camp-faibowrimo/" className="block w-full sm:w-auto mt-6">
            <Button className="py-4 px-10 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold shadow-md">
              See the Live Academy
            </Button>
          </a>
        </Card>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Hub webhook — simple request shape, matching what actually worked for
     Camp FaiBoWriMo. Secret in body only. Returns the parsed response so the
     caller can react to auth_token / requires_verification.
     ───────────────────────────────────────────────────────────────────────── */
  const fireHubWebhook = async (payload) => {
    const secret = import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_camp_secure_sync_2026_xyz';
    try {
      const res = await fetch(HUB_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secret,
          app_slug: 'gwaacademy',
          ...payload,
        }),
      });

      if (!res.ok) {
        console.warn('Hub webhook returned', res.status);
        return null;
      }

      const data = await res.json();

      if (data?.auth_token) setAuthToken(data.auth_token);
      if (data?.requires_verification) setNeedsVerification(true);
      if (data?.token_error) console.warn('Hub token error:', data.token_error);

      return data;
    } catch (webhookErr) {
      console.warn('Hub webhook failed', webhookErr);
      return null;
    }
  };

  const handleNewUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill out all fields.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();
      const existingRes = await api.get('students_gwaacademy');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      if (rows.find(u => (u.email || '').trim().toLowerCase() === cleanEmail)) {
        setError('That email already has an Academy account.');
        setLoading(false);
        return;
      }

      const trailStart = computeTrailStartDate();
      const createRes = await api.post('students_gwaacademy', {
        name: form.name,
        email: cleanEmail,
        role: 'student',
        pass_type: 'evergreen',
        trail_start_date: trailStart,
        first_login_complete: true,
        owner_id: 'pending',
      });

      const userId = createRes?.data?.id || createRes?.id || (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
      if (userId) await api.put('students_gwaacademy', userId, { owner_id: userId });

      await fireHubWebhook({
        email: cleanEmail,
        name: form.name,
        full_name: form.name,
        password: form.password,
        trail_start_date: trailStart,
        source: 'evergreen_start',
      });

      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleExistingUser = async (e) => {
    e.preventDefault();
    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');

    const cleanEmail = existingEmail.trim().toLowerCase();
    try {
      const existingRes = await api.get('students_gwaacademy');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      const existing = rows.find(u => (u.email || '').trim().toLowerCase() === cleanEmail);

      const trailStart = computeTrailStartDate();

      if (!existing) {
        const createRes = await api.post('students_gwaacademy', {
          name: '',
          email: cleanEmail,
          role: 'student',
          pass_type: 'evergreen',
          trail_start_date: trailStart,
          first_login_complete: true,
          owner_id: 'pending',
        });
        const newId = createRes?.data?.id || createRes?.id || (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
        if (newId) await api.put('students_gwaacademy', newId, { owner_id: newId });
      } else if (existing.pass_type === 'free') {
        await api.put('students_gwaacademy', existing.id, {
          pass_type: 'evergreen',
          trail_start_date: trailStart,
        });
      }

      const hubResult = await fireHubWebhook({
        email: cleanEmail,
        action: 'grant_access',
        trail_start_date: trailStart,
        source: 'existing_hub_user',
      });

      // If the Hub never answered, the camper has no permission row yet.
      if (!hubResult || !hubResult.success) {
        setError('We could not reach the GospelWise Hub. Please try again, or email jeremy@crossandpen.com.');
        setLoading(false);
        return;
      }

      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const enterCamp = () => {
    if (authToken) {
      window.location.href = `${CAMP_APP_URL}/?token=${encodeURIComponent(authToken)}`;
    } else {
      window.location.href = HUB_LOGIN_URL;
    }
  };

  const goToAapCheckout = () => {
    window.location.href = `${EVERGREEN_AAP_CHECKOUT_URL}?email=${encodeURIComponent(registeredEmail)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white">
          <SafeIcon name="Compass" className="text-3xl" />
        </div>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold text-dark-green">Your Week at Academy Starts Tomorrow</h1>
            <p className="text-muted text-sm">Register in under a minute — Day 1 opens at 7 AM tomorrow morning.</p>

            <div className="flex border-b border-tan mb-6">
              <button className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('new'); setError(''); }}>New Camper</button>
              <button className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('existing'); setError(''); }}>I Have a GWA Account</button>
            </div>

            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md mb-4">{error}</div>}

            {activeTab === 'new' ? (
              <form onSubmit={handleNewUser} className="space-y-4 text-left">
                <Input label="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Jane Doe" />
                <Input label="Email Address" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" />
                <Input label="Create a Password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder="••••••••" />
                <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>{loading ? 'Opening the gate...' : 'Step Into Academy — Free'}</Button>
              </form>
            ) : (
              <form onSubmit={handleExistingUser} className="space-y-6 text-left">
                <div className="bg-amber/10 p-4 rounded-md border border-amber/30 text-sm text-dark-green">Already part of the GospelWise Author family? Enter your email and we'll set your trail.</div>
                <Input label="Your GWA Email Address" type="email" value={existingEmail} onChange={e => setExistingEmail(e.target.value)} required placeholder="you@email.com" />
                <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>{loading ? 'Setting your trail...' : 'Step Into Academy — Free'}</Button>
              </form>
            )}
          </>
        )}

        {step === 2 && (
          <div className="space-y-5 py-2">
            <div className="mx-auto w-16 h-16 bg-forest-green/10 rounded-full flex items-center justify-center">
              <SafeIcon name="Tent" className="text-3xl text-forest-green" />
            </div>
            <h2 className="text-2xl font-bold text-dark-green">Welcome to Academy — You're In!</h2>
            <p className="text-muted leading-relaxed">Today is your prep day. Day 1 of your Study Hall workbook opens tomorrow morning at 7 AM your local time.</p>

            {needsVerification && (
              <div className="bg-forest-green/10 border border-forest-green/30 rounded-md p-4 text-sm text-dark-green text-left">
                <span className="font-bold">One quick step:</span> check your inbox for a verification email from GospelWise Author and click the link to activate your account. You'll need it to get into academy.
              </div>
            )}

            <div className="bg-amber/10 border border-amber/30 rounded-md p-5 text-left space-y-3">
              <p className="font-bold text-dark-green">One-time offer — the All-Access Pass, just $9 today</p>
              <p className="text-sm text-dark-green leading-relaxed text-xs">Keep everything you build forever plus personal daily emails, the private podcast, and more.</p>
              <Button className="w-full py-3 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold" onClick={goToAapCheckout}>Add All-Access — $9</Button>
            </div>
            <Button variant="outline" className="w-full py-3 border-2 border-forest-green" onClick={enterCamp}>Continue to Academy — Free</Button>
          </div>
        )}
      </Card>
    </div>
  );
}